// ============================================================================
// REACT QUERY HOOKS FOR CANVAS ARTIFACTS
// Replaces SPARQL-based useCanvasInstances with artifact API
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { artifactsApi, sparqlApi } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'
import { SPARQL_PREFIXES } from '@/lib/ontology/namespaces'
import type {
  CanvasInstanceSummary,
  CanvasSectionData,
  LinkedCanvasItem,
  CreateCanvasInstanceInput,
  UpdateCanvasInstanceInput,
  CanvasInstance,
} from '@/types/canvas'
import type { Artifact, ArtifactEntityLink, CanvasContent, CanvasSectionContent } from '@ispot/shared'

// ============================================================================
// ADAPTER FUNCTIONS
// Convert between Artifact API and Canvas types for component compatibility
// ============================================================================

function artifactToCanvasInstance(artifact: Artifact): CanvasInstance {
  return {
    uri: artifact.id, // UUID instead of RDF URI
    templateId: artifact.subType,
    name: artifact.title,
    description: artifact.description ?? undefined,
    projectId: artifact.projectId,
    status: artifact.status as 'draft' | 'active' | 'archived',
    createdAt: artifact.createdAt,
    updatedAt: artifact.updatedAt,
    createdBy: artifact.createdById,
  }
}

function artifactToCanvasSummary(artifact: Artifact): CanvasInstanceSummary {
  const content = artifact.content as CanvasContent
  const sections = content?.sections || {}

  // Count linked items per section
  const sectionCounts: Record<string, number> = {}
  let totalLinkedItems = 0

  for (const [sectionId, section] of Object.entries(sections)) {
    const count = section.items?.length || 0
    sectionCounts[sectionId] = count
    totalLinkedItems += count
  }

  return {
    uri: artifact.id,
    templateId: artifact.subType,
    name: artifact.title,
    description: artifact.description ?? undefined,
    status: artifact.status as 'draft' | 'active' | 'archived',
    updatedAt: artifact.updatedAt,
    totalLinkedItems,
    sectionCounts,
  }
}

function contentToSectionData(
  artifactId: string,
  content: CanvasContent,
  entityLinks: ArtifactEntityLink[]
): CanvasSectionData[] {
  const sections = content?.sections || {}
  const result: CanvasSectionData[] = []

  // Collect all unique section IDs from both content and entity links
  const allSectionIds = new Set<string>([
    ...Object.keys(sections),
    ...entityLinks.map(link => link.sectionId).filter((id): id is string => !!id),
  ])

  for (const sectionId of allSectionIds) {
    const section = sections[sectionId]

    // Get entity links for this section
    const sectionLinks = entityLinks.filter(link => link.sectionId === sectionId)

    const linkedItems: LinkedCanvasItem[] = sectionLinks.map(link => ({
      instanceUri: link.entityUri,
      label: link.entityLabel,
      classUri: link.entityType,
      addedAt: link.createdAt,
      addedBy: link.createdById ?? undefined,
      sortOrder: link.sortOrder,
      note: link.contextNote ?? undefined,
    }))

    // Sort by sortOrder
    linkedItems.sort((a, b) => a.sortOrder - b.sortOrder)

    result.push({
      sectionId,
      canvasUri: artifactId,
      linkedItems,
      notes: section?.notes || '',
    })
  }

  return result
}

// ============================================================================
// CONTEXT HOOKS
// ============================================================================

function useContext() {
  const { accountId, projectId } = useSessionStore()
  return { accountId, projectId, hasContext: !!accountId && !!projectId }
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get all canvas instances, optionally filtered by template
 */
export function useCanvasInstances(templateId?: string) {
  const { accountId, projectId, hasContext } = useContext()

  return useQuery({
    queryKey: ['canvasArtifacts', accountId, projectId, templateId],
    queryFn: async () => {
      if (!accountId || !projectId) return []

      const { artifacts } = await artifactsApi.list(accountId, projectId, {
        type: 'Canvas',
        subType: templateId,
        status: 'active',
      })

      return artifacts.map(artifactToCanvasSummary)
    },
    staleTime: 30000, // 30 seconds
    enabled: hasContext,
  })
}

/**
 * Get a single canvas instance with full details
 */
export function useCanvasInstance(uri: string | null) {
  const { accountId, projectId } = useContext()

  return useQuery({
    queryKey: ['canvasArtifact', accountId, projectId, uri],
    queryFn: async () => {
      if (!uri || !accountId || !projectId) return null

      const { artifact } = await artifactsApi.get(accountId, projectId, uri)
      return artifactToCanvasInstance(artifact)
    },
    enabled: !!uri && !!accountId && !!projectId,
  })
}

/**
 * Get section data for a canvas instance
 */
export function useCanvasSectionData(canvasUri: string | null, _sectionId?: string) {
  const { accountId, projectId } = useContext()

  return useQuery({
    queryKey: ['canvasArtifactSections', accountId, projectId, canvasUri],
    queryFn: async () => {
      if (!canvasUri || !accountId || !projectId) return []

      // Get artifact and entity links
      const [{ artifact }, { links }] = await Promise.all([
        artifactsApi.get(accountId, projectId, canvasUri),
        artifactsApi.getEntityLinks(accountId, projectId, canvasUri),
      ])

      return contentToSectionData(
        canvasUri,
        artifact.content as CanvasContent,
        links
      )
    },
    enabled: !!canvasUri && !!accountId && !!projectId,
  })
}

/**
 * Get instances grouped by template
 */
export function useCanvasInstancesGrouped() {
  const { data: instances, ...rest } = useCanvasInstances()

  const grouped = instances?.reduce((acc, instance) => {
    const existing = acc.find((g) => g.templateId === instance.templateId)
    if (existing) {
      existing.instances.push(instance)
    } else {
      acc.push({
        templateId: instance.templateId,
        templateName: instance.templateId,
        instances: [instance],
      })
    }
    return acc
  }, [] as { templateId: string; templateName: string; instances: CanvasInstanceSummary[] }[])

  return { data: grouped, ...rest }
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new canvas instance
 */
export function useCreateCanvasInstance() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async (input: CreateCanvasInstanceInput) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get domain from template (default to Business for canvas)
      const domain = getCanvasDomain(input.templateId)

      const { artifact } = await artifactsApi.create(accountId, projectId, {
        type: 'Canvas',
        subType: input.templateId,
        domain,
        title: input.name,
        description: input.description,
        content: { sections: {} },
        status: 'active',
      })

      return artifact.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Update a canvas instance
 */
export function useUpdateCanvasInstance() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      uri,
      updates,
    }: {
      uri: string
      updates: UpdateCanvasInstanceInput
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      await artifactsApi.update(accountId, projectId, uri, {
        title: updates.name,
        description: updates.description,
        status: updates.status,
      })
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
    },
  })
}

/**
 * Delete (archive) a canvas instance
 */
export function useDeleteCanvasInstance() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async (uri: string) => {
      if (!accountId || !projectId) throw new Error('No context')
      await artifactsApi.archive(accountId, projectId, uri)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Duplicate a canvas instance
 */
export function useDuplicateCanvasInstance() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      uri,
      newName,
    }: {
      uri: string
      newName: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get original artifact
      const { artifact } = await artifactsApi.get(accountId, projectId, uri)
      const { links } = await artifactsApi.getEntityLinks(accountId, projectId, uri)

      // Create new artifact with same content
      const { artifact: newArtifact } = await artifactsApi.create(accountId, projectId, {
        type: 'Canvas',
        subType: artifact.subType,
        domain: artifact.domain,
        title: newName,
        description: artifact.description ?? undefined,
        content: artifact.content,
      })

      // Copy entity links
      for (const link of links) {
        await artifactsApi.linkEntity(accountId, projectId, newArtifact.id, {
          entityUri: link.entityUri,
          entityType: link.entityType,
          entityLabel: link.entityLabel,
          sectionId: link.sectionId ?? undefined,
          contextNote: link.contextNote ?? undefined,
          sortOrder: link.sortOrder,
        })
      }

      return newArtifact.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

// ============================================================================
// SECTION MUTATION HOOKS
// ============================================================================

/**
 * Update section notes
 */
export function useUpdateSectionNotes() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
      notes,
    }: {
      canvasUri: string
      sectionId: string
      notes: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get current artifact
      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent

      // Update section notes
      const updatedContent: CanvasContent = {
        ...content,
        sections: {
          ...content.sections,
          [sectionId]: {
            ...content.sections?.[sectionId],
            notes,
          },
        },
      }

      await artifactsApi.update(accountId, projectId, canvasUri, {
        content: updatedContent,
      })
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
    },
  })
}

/**
 * Link an item to a section
 */
export function useLinkItemToSection() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
      itemUri,
      itemLabel,
      itemType,
      note,
    }: {
      canvasUri: string
      sectionId: string
      itemUri: string
      itemLabel?: string
      itemType?: string
      note?: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get current links to determine sort order
      const { links } = await artifactsApi.getEntityLinks(accountId, projectId, canvasUri, sectionId)
      const maxSortOrder = links.reduce((max, link) => Math.max(max, link.sortOrder), -1)

      await artifactsApi.linkEntity(accountId, projectId, canvasUri, {
        entityUri: itemUri,
        entityType: itemType || 'Unknown',
        entityLabel: itemLabel || itemUri.split('/').pop() || 'Unknown',
        sectionId,
        contextNote: note,
        sortOrder: maxSortOrder + 1,
      })

      // Ensure section exists in content
      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent

      if (!content.sections?.[sectionId]) {
        const updatedContent: CanvasContent = {
          ...content,
          sections: {
            ...content.sections,
            [sectionId]: { notes: '' },
          },
        }
        await artifactsApi.update(accountId, projectId, canvasUri, {
          content: updatedContent,
        })
      }
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Unlink an item from a section
 */
export function useUnlinkItemFromSection() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
      itemUri,
    }: {
      canvasUri: string
      sectionId: string
      itemUri: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Find the link to remove
      const { links } = await artifactsApi.getEntityLinks(accountId, projectId, canvasUri, sectionId)
      const linkToRemove = links.find(link =>
        link.entityUri === itemUri && link.sectionId === sectionId
      )

      if (linkToRemove) {
        await artifactsApi.unlinkEntity(accountId, projectId, canvasUri, linkToRemove.id)
      }
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Create a NEW entity in the knowledge graph AND link it to a canvas section
 * Use this for coach suggestions where the entity doesn't exist yet
 */
export function useCreateAndLinkItem() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
      label,
      entityType,
      description,
      note,
      source,
      confidence,
    }: {
      canvasUri: string
      sectionId: string
      label: string
      entityType: string
      description?: string
      note?: string
      source?: 'user' | 'ai' | 'document' | 'import'
      confidence?: number
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get current links to determine sort order
      const { links } = await artifactsApi.getEntityLinks(accountId, projectId, canvasUri, sectionId)
      const maxSortOrder = links.reduce((max, link) => Math.max(max, link.sortOrder), -1)

      // Create entity in Fuseki AND link to canvas
      const result = await artifactsApi.createAndLinkEntity(accountId, projectId, canvasUri, {
        sectionId,
        label,
        entityType,
        description,
        note,
        sortOrder: maxSortOrder + 1,
        source,
        confidence,
      })

      // Ensure section exists in content
      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent

      if (!content.sections?.[sectionId]) {
        const updatedContent: CanvasContent = {
          ...content,
          sections: {
            ...content.sections,
            [sectionId]: { notes: '', items: [] },
          },
        }
        await artifactsApi.update(accountId, projectId, canvasUri, {
          content: updatedContent,
        })
      }

      return result
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      // Invalidate canvas queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
      // Also invalidate tree view data to show new entities
      queryClient.invalidateQueries({ queryKey: ['ontologyInstances'] })
      queryClient.invalidateQueries({ queryKey: ['entityCounts'] })
      // Cross-invalidate entity tree so Flow view and Entity Tree update
      queryClient.invalidateQueries({ queryKey: ['entityTree'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

// ============================================================================
// PHOTO HOOKS
// ============================================================================

/**
 * Get section photo URL
 */
export function useSectionPhoto(canvasUri: string | null, sectionId: string) {
  const { accountId, projectId } = useContext()

  return useQuery({
    queryKey: ['canvasArtifactPhoto', accountId, projectId, canvasUri, sectionId],
    queryFn: async () => {
      if (!canvasUri || !accountId || !projectId) return null

      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent
      return content?.sections?.[sectionId]?.photoUrl || null
    },
    enabled: !!canvasUri && !!accountId && !!projectId,
  })
}

/**
 * Update section photo
 */
export function useUpdateSectionPhoto() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
      photoUrl,
    }: {
      canvasUri: string
      sectionId: string
      photoUrl: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent

      const updatedContent: CanvasContent = {
        ...content,
        sections: {
          ...content.sections,
          [sectionId]: {
            ...content.sections?.[sectionId],
            notes: content.sections?.[sectionId]?.notes || '',
            items: content.sections?.[sectionId]?.items || [],
            photoUrl,
          },
        },
      }

      await artifactsApi.update(accountId, projectId, canvasUri, {
        content: updatedContent,
      })
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactPhoto'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Delete section photo
 */
export function useDeleteSectionPhoto() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sectionId,
    }: {
      canvasUri: string
      sectionId: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      const { artifact } = await artifactsApi.get(accountId, projectId, canvasUri)
      const content = artifact.content as CanvasContent

      // Remove photoUrl from section
      const section = content.sections?.[sectionId]
      if (section) {
        const { photoUrl: _, ...sectionWithoutPhoto } = section as CanvasSectionContent & { photoUrl?: string }
        const updatedContent: CanvasContent = {
          ...content,
          sections: {
            ...content.sections,
            [sectionId]: sectionWithoutPhoto,
          },
        }

        await artifactsApi.update(accountId, projectId, canvasUri, {
          content: updatedContent,
        })
      }
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactPhoto'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

// ============================================================================
// AUTO-POPULATE HOOKS
// ============================================================================

/**
 * Auto-populate a canvas with entities from the graph
 * Note: Still uses SPARQL for entity discovery, but stores in artifact system
 */
export function useAutoPopulateCanvas() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sections,
    }: {
      canvasUri: string
      sections: Array<{ sectionId: string; classUris: string[] }>
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      let sectionsPopulated = 0
      let entitiesLinked = 0

      for (const section of sections) {
        if (section.classUris.length === 0) continue

        // Build filter for all class URIs in this section
        const classFilters = section.classUris
          .map((classUri) => `?classUri = <${classUri}>`)
          .join(' || ')

        // Query SPARQL for entities matching any of the class URIs
        const sparql = `${SPARQL_PREFIXES}
          SELECT DISTINCT ?entityUri ?label ?classUri
          WHERE {
            ?entityUri a ?classUri ;
                       rdfs:label ?label .
            FILTER(${classFilters})
          }
          ORDER BY ?label
          LIMIT 50
        `
        const results = await sparqlApi.query(accountId, projectId, sparql)

        if (results.results.bindings.length > 0) {
          sectionsPopulated++

          // Link each entity to the canvas section via artifact API
          for (const binding of results.results.bindings) {
            const entityUri = binding.entityUri?.value
            const label = binding.label?.value
            const classUri = binding.classUri?.value

            if (entityUri && label) {
              try {
                // Get current links to determine sort order
                const { links } = await artifactsApi.getEntityLinks(accountId, projectId, canvasUri, section.sectionId)
                const maxSortOrder = links.reduce((max, link) => Math.max(max, link.sortOrder), -1)

                await artifactsApi.linkEntity(accountId, projectId, canvasUri, {
                  entityUri,
                  entityType: classUri || 'Unknown',
                  entityLabel: label,
                  sectionId: section.sectionId,
                  sortOrder: maxSortOrder + 1,
                })
                entitiesLinked++
              } catch (error) {
                console.warn(`Failed to link ${label} to section ${section.sectionId}:`, error)
              }
            }
          }
        }
      }

      return { sectionsPopulated, entitiesLinked }
    },
    onSuccess: () => {
      // Use partial key matching to avoid stale closure issues
      queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

/**
 * Sync canvas with new entities from graph (only links entities not already linked)
 * Call this when canvas loads to pick up entities created elsewhere (e.g., Entity Tree)
 */
export function useSyncCanvasEntities() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      canvasUri,
      sections,
    }: {
      canvasUri: string
      sections: Array<{ sectionId: string; classUris: string[] }>
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get ALL existing links for this canvas once
      const { links: existingLinks } = await artifactsApi.getEntityLinks(accountId, projectId, canvasUri)
      const linkedEntityUris = new Set(existingLinks.map(link => link.entityUri))

      let newEntitiesLinked = 0

      for (const section of sections) {
        if (section.classUris.length === 0) continue

        const classFilters = section.classUris
          .map((classUri) => `?classUri = <${classUri}>`)
          .join(' || ')

        const sparql = `${SPARQL_PREFIXES}
          SELECT DISTINCT ?entityUri ?label ?classUri
          WHERE {
            ?entityUri a ?classUri ;
                       rdfs:label ?label .
            FILTER(${classFilters})
          }
          ORDER BY ?label
          LIMIT 50
        `
        const results = await sparqlApi.query(accountId, projectId, sparql)

        // Get section's current links for sort order
        const sectionLinks = existingLinks.filter(link => link.sectionId === section.sectionId)
        let maxSortOrder = sectionLinks.reduce((max, link) => Math.max(max, link.sortOrder), -1)

        for (const binding of results.results.bindings) {
          const entityUri = binding.entityUri?.value
          const label = binding.label?.value
          const classUri = binding.classUri?.value

          // Skip if already linked anywhere in this canvas
          if (!entityUri || !label || linkedEntityUris.has(entityUri)) continue

          try {
            await artifactsApi.linkEntity(accountId, projectId, canvasUri, {
              entityUri,
              entityType: classUri || 'Unknown',
              entityLabel: label,
              sectionId: section.sectionId,
              sortOrder: ++maxSortOrder,
            })
            linkedEntityUris.add(entityUri) // Track so we don't link same entity twice
            newEntitiesLinked++
          } catch (error) {
            // Silently skip failed links (might be duplicate constraint)
          }
        }
      }

      return { newEntitiesLinked }
    },
    onSuccess: (result) => {
      // Only invalidate if we actually linked something
      // Use partial key matching to avoid stale closure issues
      if (result.newEntitiesLinked > 0) {
        queryClient.invalidateQueries({ queryKey: ['canvasArtifactSections'] })
        queryClient.invalidateQueries({ queryKey: ['canvasArtifact'] })
      }
    },
  })
}

/**
 * Ensure default canvas exists for a template
 */
export function useEnsureDefaultCanvas() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()
  const createCanvas = useCreateCanvasInstance()

  return useMutation({
    mutationFn: async ({
      templateId,
      templateName,
      canvasType: _canvasType,
      sections,
    }: {
      templateId: string
      templateName: string
      canvasType: string
      sections: Array<{ sectionId: string; classUris: string[] }>
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Check if active canvas exists (ignore draft canvases)
      const { artifacts } = await artifactsApi.list(accountId, projectId, {
        type: 'Canvas',
        subType: templateId,
        status: 'active',
      })

      if (artifacts.length > 0) {
        // Return existing active canvas
        return { uri: artifacts[0].id, created: false }
      }

      // Create new canvas
      const newUri = await createCanvas.mutateAsync({
        templateId,
        name: `Default ${templateName}`,
      })

      // Auto-populate canvas with entities from the graph
      let entitiesLinked = 0
      for (const section of sections) {
        if (section.classUris.length === 0) continue

        const classFilters = section.classUris
          .map((classUri) => `?classUri = <${classUri}>`)
          .join(' || ')

        try {
          const sparql = `${SPARQL_PREFIXES}
            SELECT DISTINCT ?entityUri ?label ?classUri
            WHERE {
              ?entityUri a ?classUri ;
                         rdfs:label ?label .
              FILTER(${classFilters})
            }
            ORDER BY ?label
            LIMIT 50
          `
          const results = await sparqlApi.query(accountId, projectId, sparql)

          for (const binding of results.results.bindings) {
            const entityUri = binding.entityUri?.value
            const label = binding.label?.value
            const classUri = binding.classUri?.value

            if (entityUri && label) {
              try {
                const { links } = await artifactsApi.getEntityLinks(accountId, projectId, newUri, section.sectionId)
                const maxSortOrder = links.reduce((max, link) => Math.max(max, link.sortOrder), -1)

                await artifactsApi.linkEntity(accountId, projectId, newUri, {
                  entityUri,
                  entityType: classUri || 'Unknown',
                  entityLabel: label,
                  sectionId: section.sectionId,
                  sortOrder: maxSortOrder + 1,
                })
                entitiesLinked++
              } catch (error) {
                console.warn(`Failed to link ${label} to section ${section.sectionId}:`, error)
              }
            }
          }
        } catch (error) {
          console.warn(`Failed to query entities for section ${section.sectionId}:`, error)
        }
      }

      console.log(`Auto-populated default canvas with ${entitiesLinked} entities`)
      return { uri: newUri, created: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasArtifacts'] })
    },
  })
}

// ============================================================================
// HELPERS
// ============================================================================

function getCanvasDomain(templateId: string): 'Market' | 'Business' | 'Solution' {
  // Map template IDs to domains
  const marketTemplates = ['customer-empathy-map', 'customer-jobs-map', 'persona-canvas']
  const solutionTemplates = ['lean-canvas', 'product-canvas']

  if (marketTemplates.includes(templateId)) return 'Market'
  if (solutionTemplates.includes(templateId)) return 'Solution'
  return 'Business' // Default for business model canvas, value proposition, etc.
}
